"use client";

import { Select as SelectPrimitive } from "radix-ui";
import { RiArrowDownSLine, RiArrowUpSLine } from "react-icons/ri";

import { Skeleton } from "@/components/atoms/Skeleton";
import { tv } from "@/lib/tailwind-variants";
import { cn } from "@/lib/utils";

const selectTriggerVariants = tv({
  slots: {
    base: "flex h-8.5 w-full items-center justify-between gap-1 rounded-sm border border-gray-300 bg-white p-2 text-Medium leading-tight text-gray-700 placeholder:text-gray-300 invalid:border-error-500 focus-visible:ring-2 focus-visible:ring-info-500/50 focus-visible:outline-hidden disabled:cursor-not-allowed disabled:bg-gray-100",
    item: "relative flex w-full cursor-default items-center border-b border-gray-200 px-4 py-2 text-Medium outline-hidden select-none last:border-transparent focus:bg-primary-50 data-disabled:pointer-events-none data-disabled:opacity-50",
    skelton: "block h-8.5 w-full rounded-sm",
  },
});

const { base, item, skelton } = selectTriggerVariants();

const Select = SelectPrimitive.Root;

const SelectValue = SelectPrimitive.Value;

const SelectTrigger = ({
  asChild = false,
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger>) => {
  if (asChild) {
    return (
      <SelectPrimitive.Trigger asChild {...props}>
        {children}
      </SelectPrimitive.Trigger>
    );
  }

  return (
    <SelectPrimitive.Trigger className={cn(base(), className)} {...props}>
      {children}
      <SelectPrimitive.Icon asChild>
        <RiArrowDownSLine size={20} className="flex-none text-gray-500" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
};

const SelectContent = ({
  className,
  children,
  position = "popper",
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content>) => (
  <SelectPrimitive.Portal>
    <SelectPrimitive.Content
      className={cn(
        "relative min-w-36 overflow-hidden rounded-sm border border-gray-200 bg-white text-gray-700 shadow-info-popup data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95",
        position === "popper" &&
          "max-h-(--radix-select-content-available-height) w-(--radix-select-trigger-width) data-[side=bottom]:translate-y-1 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1",
        className
      )}
      position={position}
      {...props}
    >
      <SelectPrimitive.ScrollUpButton className="flex h-6 cursor-default items-center justify-center bg-white text-primary-600">
        <RiArrowUpSLine />
      </SelectPrimitive.ScrollUpButton>
      <SelectPrimitive.Viewport
        className={cn(
          position === "popper" &&
            "h-(--radix-select-trigger-height) w-full min-w-(--radix-select-trigger-width)"
        )}
      >
        {children}
      </SelectPrimitive.Viewport>
      <SelectPrimitive.ScrollDownButton className="flex h-[25px] cursor-default items-center justify-center bg-white text-primary-600">
        <RiArrowDownSLine />
      </SelectPrimitive.ScrollDownButton>
    </SelectPrimitive.Content>
  </SelectPrimitive.Portal>
);

const SelectItem = ({
  className,
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item>) => {
  return (
    <SelectPrimitive.Item className={cn(item(), className)} {...props}>
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
};

const SelectSkelton = ({
  className,
  ...props
}: React.ComponentProps<typeof Skeleton>) => {
  return <Skeleton className={cn(skelton(), className)} {...props} />;
};

export {
  Select,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectSkelton,
};
